<script setup>
import BackButton from "@/components/utils/BackButton.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import LoadingArea from "@/components/utils/LoadingArea.vue";
import apiCall from "@/utils/api";
import { ref, computed, onMounted } from "vue";
import { hasPermission, EPORTAL_SCHEME_PLAN_APPLICATION_CREATE } from "@/utils/permissions";

const props = defineProps({
  application: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["goBackClicked", "resolved"]);

const loading = ref(true);
const fullApplication = ref(null);
const categories = ref([]);
const types = ref([]);

const amendCategory = ref(null);
const amendType = ref(null);
const amendRemarks = ref("");
const amendFiles = ref({});
const submitting = ref(false);
const snackbar = ref(false);
const snackbarMessage = ref("");
const snackbarColor = ref("");

onMounted(async () => {
  try {
    const [appResp, formResp] = await Promise.all([
      apiCall({
        url: `/api/client/scheme-plan-application/${props.application.id}`,
        method: "GET",
      }),
      apiCall({
        url: "/api/client/scheme-plan-application/form-data",
        method: "GET",
      }),
    ]);
    fullApplication.value = appResp;
    categories.value = formResp.categories || [];
    types.value = formResp.types || [];
    amendCategory.value = appResp.scheme_plan_category_id;
    amendType.value = appResp.scheme_plan_type_id;
    amendRemarks.value = appResp.remarks || "";
  } catch (e) {
    console.error("Failed to load resolve data", e);
  } finally {
    loading.value = false;
  }
});

const feedbacks = computed(() => fullApplication.value?.document_feedbacks || []);
const hasFeedbacks = computed(() => feedbacks.value.length > 0);

function getLatestComment(feedback) {
  if (!feedback?.feedback?.length) return "";
  return feedback.feedback[feedback.feedback.length - 1]?.comment || "";
}

function getFeedbackDate(feedback) {
  if (!feedback?.feedback?.length) return "";
  const entry = feedback.feedback[feedback.feedback.length - 1];
  return entry?.created_at ? formatDate(entry.created_at) : "";
}

function getDocumentsForFeedback(fb) {
  if (!fullApplication.value?.documents || !fb.reattach_document) return [];
  return fullApplication.value.documents.filter(
    (doc) => (doc.supporting_document?.name || doc.original_name) === fb.document_name
  );
}

function onFileSelected(docId, event) {
  const file = event?.target?.files?.[0] || event;
  if (file) {
    amendFiles.value[docId] = file;
  }
}

const isDeferred = computed(
  () => (fullApplication.value?.status || "").toLowerCase() === "deferred"
);
const canCreateApplication = computed(() => hasPermission(EPORTAL_SCHEME_PLAN_APPLICATION_CREATE));

async function submitResolution() {
  submitting.value = true;
  try {
    const formData = new FormData();
    formData.append("scheme_plan_category_id", amendCategory.value);
    formData.append("scheme_plan_type_id", amendType.value);
    if (amendRemarks.value) {
      formData.append("remarks", amendRemarks.value);
    }
    for (const [docId, file] of Object.entries(amendFiles.value)) {
      formData.append(`document_${docId}`, file);
    }

    await apiCall({
      url: `/api/client/scheme-plan-application/${props.application.id}/amend`,
      data: formData,
      method: "POST",
    });

    snackbarMessage.value =
      "Query resolved. Application re-submitted for approval.";
    snackbarColor.value = "success";
    snackbar.value = true;
    setTimeout(() => emit("resolved"), 1500);
  } catch (error) {
    snackbarMessage.value = error?.response?.data?.errors
      ? Object.values(error.response.data.errors).flat()[0]
      : "An error occurred. Please try again.";
    snackbarColor.value = "error";
    snackbar.value = true;
  } finally {
    submitting.value = false;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getStatusColor(status) {
  const s = (status || "").toLowerCase();
  if (s === "approved") return "success";
  if (s === "rejected") return "error";
  if (s === "pending") return "warning";
  if (s === "deferred") return "deep-orange";
  if (s === "in_progress") return "info";
  return "grey";
}
</script>

<template>
  <v-container fluid class="pa-0">
    <!-- Header -->
    <v-row>
      <v-col cols="12" md="1">
        <BackButton @click="emit('goBackClicked')">Back</BackButton>
      </v-col>
      <v-col>
        <div class="text-h6 text-center font-weight-bold primary--text">
          Resolve Query — {{ application.application_number }}
        </div>
      </v-col>
    </v-row>

    <LoadingArea :loading="loading">
      <template v-if="fullApplication">
        <!-- Application Summary -->
        <v-card outlined class="pa-5 mt-2">
          <v-row>
            <v-col cols="12" md="4">
              <LabelledField label="Application Number">
                <span class="font-weight-bold">{{ fullApplication.application_number }}</span>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField label="Status">
                <v-chip small :color="getStatusColor(fullApplication.status)" dark>
                  {{ fullApplication.status }}
                </v-chip>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField label="Property (PN)">
                <span>{{ fullApplication.scheme_plan_property?.pn || "-" }}</span>
              </LabelledField>
            </v-col>
          </v-row>
        </v-card>

        <!-- Raised Issues -->
        <v-card outlined class="pa-5 mt-4">
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left color="warning">mdi-alert-circle</v-icon>
            Raised Issues
          </div>

          <template v-if="hasFeedbacks">
            <v-card
              v-for="fb in feedbacks"
              :key="fb.id"
              outlined
              class="mb-3 pa-3"
              :color="fb.reattach_document ? 'red lighten-5' : 'green lighten-5'"
            >
              <v-row align="center" no-gutters>
                <v-col cols="12" md="9">
                  <div class="font-weight-medium">
                    <v-icon small :color="fb.reattach_document ? 'error' : 'success'" class="mr-1">
                      {{ fb.reattach_document ? "mdi-file-alert" : "mdi-check-circle" }}
                    </v-icon>
                    {{ fb.document_name || "General Issue" }}
                  </div>
                  <div class="text-body-2 mt-1 grey--text text--darken-2">
                    {{ getLatestComment(fb) }}
                  </div>
                  <div v-if="getFeedbackDate(fb)" class="text-caption grey--text mt-1">
                    Raised: {{ getFeedbackDate(fb) }}
                  </div>
                </v-col>
                <v-col cols="12" md="3" class="text-md-right mt-2 mt-md-0">
                  <v-chip
                    v-if="fb.reattach_document"
                    small
                    color="error"
                    dark
                  >
                    Document Re-upload Required
                  </v-chip>
                  <v-chip v-else small color="success" dark>
                    Document Okay
                  </v-chip>
                </v-col>
              </v-row>
            </v-card>
          </template>

          <v-alert v-else type="info" dense outlined>
            No specific feedback found. The application was deferred — please update the required information below.
          </v-alert>
        </v-card>

        <!-- Resolution Form (only when deferred) -->
        <v-card v-if="isDeferred" outlined class="pa-5 mt-4">
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left color="primary">mdi-pencil-box</v-icon>
            Your Response
          </div>

          <v-row v-if="canCreateApplication">
            <v-col cols="12" md="6">
              <v-select
                v-model="amendCategory"
                :items="categories"
                item-text="name"
                item-value="id"
                label="Category"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="amendType"
                :items="types"
                item-text="name"
                item-value="id"
                label="Type"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <!-- Document re-uploads for each flagged feedback -->
          <template v-for="fb in feedbacks">
            <template v-if="fb.reattach_document">
              <v-row
                v-for="doc in getDocumentsForFeedback(fb)"
                :key="'resolve-' + doc.id"
                class="align-center"
              >
                <v-col cols="12" md="4">
                  <span class="font-weight-medium">
                    {{ doc.supporting_document?.name || doc.original_name }}
                  </span>
                  <div class="text-caption error--text">
                    {{ getLatestComment(fb) }}
                  </div>
                </v-col>
                <v-col cols="12" md="8">
                  <v-file-input
                    :label="'Re-upload: ' + (doc.supporting_document?.name || doc.original_name)"
                    outlined
                    dense
                    accept="application/pdf"
                    @change="onFileSelected(doc.scheme_plan_supporting_document_id, $event)"
                  />
                </v-col>
              </v-row>
            </template>
          </template>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="amendRemarks"
                label="Response / Remarks"
                outlined
                dense
                auto-grow
                rows="3"
                placeholder="Describe how you have addressed each raised issue..."
              />
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12">
              <v-btn
                color="primary"
                :loading="submitting"
                class="text-none"
                @click="submitResolution"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Submit Resolution
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-card v-else outlined class="pa-5 mt-4">
          <v-alert type="warning" dense outlined>
            This application is no longer in a deferred state and cannot be amended at this time.
          </v-alert>
        </v-card>
      </template>
    </LoadingArea>

    <v-snackbar v-model="snackbar" top :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>
